import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, TextInput, StyleSheet, ActivityIndicator } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import moment from 'moment';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Bar, Line } from 'react-chartjs-2';
import { INK, MUTED } from '../styles/chartColors';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement,
    LineElement, PointElement, Filler, Title, Tooltip, Legend
} from 'chart.js';
import {
    selectedJobAtom, selectedJobDataAtom, selectedVersionAtom, jobUserDataAtom,
    jobCostDetailAtom, jobBillingsAtom, jobOpenCommitmentsAtom, selectedVersionLockedAtom, isLiveVersionAtom,
    jobPhaseCodesAtom,
    dataSourceVersionAtom, userIdAtom, userNameAtom, userEmailAtom, projectCompleteAtom,
    sideMenuBudgetsAtom, sideMenuCostsAtom
} from '../store/company';
import { loadUserData, updateUserDataArray, fNum, pNum } from '../db/company';
import {
    TAB, HORIZON_ROW_ID, BURN_TABS, REVENUE_RANGE, ROWS, cellId,
    burnRowWeek, burnRowEst, overrideMonthOf, monthsOf, horizonEndOf, revenueMonthlyFrom,
    burnMonthlyFrom, actualsMonthlyFrom, savedByRowIdOf, nodeIdsByBaseOf, etcPoolByRowOf, jobProfitPctOf,
} from '../helpers/revenueCurve';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Title, Tooltip, Legend);

// The Revenue Curve Forecast tab: past-month actuals plus a user-spread ETC for
// the third-party rows, with Labor and Equipment pulled read-only from the Burn
// Tool. Persisted under Forecast_Tab 'Revenue Curve'; the PSR slide renders this
// same component with `photo` set. Its month math lives in helpers/revenueCurve.js.

// Chart panel and table panel share the same title+padding chrome (a
// panelTitle line plus 14px of padding top and bottom) — used to back out
// just the chart figure's own height from the target panel height below.
const PANEL_CHROME = 49;

// Revenue fill on the cumulative chart — one flat color, no actual/forecast
// split (that distinction is bar-chart only).
// Two overlay lines on top of the five cost series: ink and grey, named in the legend
const REVENUE_COLOR = INK;
const TOTAL_EAC_COLOR = MUTED;

const rangeFor = (row) => row.range || row.curveRange;

// Every cost-type range this page rolls up, used to scope the filter dropdowns
// to codes the curve can actually show.
const CURVE_RANGES = ROWS.map(rangeFor);
const inCurveRanges = (ct) => CURVE_RANGES.some(([lo, hi]) => ct >= lo && ct <= hi);

const rowVendor = (r) => String(r?.Supplier_Name || '');
const rowPhase = (r) => String(r?.Phase_Code || '');
// Cost detail rows rarely carry Resource_Code directly — it resolves from the
// purchasing reporting codes, same chain the third-party pages use.
const rowResource = (r) => String(
    r?.Resource_Code || r?.Purchasing_Reporting_Code_4 || r?.Purchasing_Reporting_Code_1 ||
    r?.Rental_Equipment_Classification_Tier_Name || r?.Equipment_Classification_Tier_Name || ''
);

const $ = (v) => '$' + fNum(v || 0, 1);

const CostCurveForecast = (a) => {
    const chartsRef = useRef();
    if (a?.photo && a.setJobRef) {
        a.setJobRef(a.job, 'revenueCurve', chartsRef);
    }

    // PSR slide only: measures the rendered table panel so the charts above
    // it can be sized to a fixed ratio of it (60/40) instead of a guessed
    // pixel height, which would either clip the table or leave it stranded
    // in a lot of blank space depending on how many rows/columns it has.
    const photoTablePanelRef = useRef();
    const [photoTablePanelHeight, setPhotoTablePanelHeight] = useState(null);
    useEffect(() => {
        if (!a?.photo || typeof ResizeObserver === 'undefined') return undefined;
        const el = photoTablePanelRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver((entries) => {
            const h = entries[0]?.contentRect?.height;
            if (h) setPhotoTablePanelHeight(h);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [a?.photo]);
    // 1.5x the table panel's height makes the chart panel 60% of the
    // chart+table stack (1.5 / (1.5 + 1) = 0.6). Falls back to a sane
    // default before the table has had a chance to measure itself.
    const photoChartFigureHeight = photoTablePanelHeight
        ? Math.max(200, (photoTablePanelHeight * 1.5) - PANEL_CHROME)
        : 320;
    // The table's tall, narrow money-stack rows make the chart+table stack
    // itself tall and narrow — left uncorrected, the composite ends up
    // roughly square, and addImageSlide's Revenue Curve branch (which fits
    // this image into a 9in x 4.75in box) has to shrink it to match that
    // height, stranding it in a slim centered column with large side
    // margins. Sizing the chart row's width so the *whole* stack matches
    // the slide box's own aspect ratio makes the image width-bound instead,
    // so it actually fills the box edge to edge.
    const SLIDE_CONTENT_ASPECT = 9 / 4.75;
    const CHART_TABLE_GAP = 16;
    const photoAspectWidth = photoTablePanelHeight
        ? SLIDE_CONTENT_ASPECT * ((photoTablePanelHeight * 2.5) + CHART_TABLE_GAP)
        : 1700;

    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [completionDateForecast] = useAtom(projectCompleteAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [userData, setUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobBillings] = useAtom(jobBillingsAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [versionLocked] = useAtom(selectedVersionLockedAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [dataSourceVersions] = useAtom(dataSourceVersionAtom);
    const [userId] = useAtom(userIdAtom);
    const [userName] = useAtom(userNameAtom);
    const [userEmail] = useAtom(userEmailAtom);
    const [smBudgets] = useAtom(sideMenuBudgetsAtom);
    const [smCosts] = useAtom(sideMenuCostsAtom);

    const [loading, setLoading] = useState(false);
    const [edits, setEdits] = useState({});
    const [extraMonths, setExtraMonths] = useState(0);
    // Typed end month for the chart/table window. Persisted per job/version
    // (Value_Amount = YYYYMM under a reserved row id) so the PSR slide — a
    // separate instance of this component — renders the same window. Zero /
    // absent means the computed default horizon applies.
    const overrideMonth = useMemo(() => overrideMonthOf(userData), [userData]);

    const [poolWarning, setPoolWarning] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterVendor, setFilterVendor] = useState('');
    const [filterPhase, setFilterPhase] = useState('');
    const [filterResource, setFilterResource] = useState('');
    const filterActive = !!(filterVendor || filterPhase || filterResource);

    const versionInfo = useMemo(() => (
        (Array.isArray(dataSourceVersions) ? dataSourceVersions : []).find((v) => v.Version_GUID === selectedVersion)
    ), [dataSourceVersions, selectedVersion]);

    // "Now" on this page is the selected Forecast Date, not the wall clock.
    // A forecast dated in August is still the August period when it's opened
    // in September — August is the month in progress (A+F) and September
    // onward is forecast (F). Anchoring to moment() instead closes out August
    // as pure actual and hands the A+F split to a month the forecast never
    // covered, so the ETC typed for the open month has nowhere to land.
    const forecastDate = useMemo(() => (
        versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime) : moment()
    ), [versionInfo]);
    const currentMonth = useMemo(() => forecastDate.clone().startOf('month'), [forecastDate]);
    const isPast = (m) => m.start.isBefore(currentMonth);
    // The month in progress is half closed: cost has already posted into it
    // and more is still to come, so it carries actuals *and* ETC.
    const isCurrent = (m) => m.start.isSame(currentMonth);

    // Where the Burn Tool stops trusting actuals and starts trusting the
    // estimate: a week is forecast when it starts after the version snapshot's
    // prior week. Cost for anything later was still landing when the snapshot
    // was taken, so its estimate has to stand. burnTool.js draws the line at
    // this exact date — anchoring to today instead swallows the open weeks'
    // estimates into actuals and understates Labor and Equipment ETC.
    const burnCutoff = useMemo(() => forecastDate.clone().add(-7, 'days'), [forecastDate]);

    useEffect(() => {
        if (selectedJob && selectedVersion) {
            setLoading(true);
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion })
                .then((data) => setUserData(data?.data))
                .finally(() => setLoading(false));
        }
    }, [selectedJob, selectedVersion]);

    // ---- Month columns ---------------------------------------------------
    const months = useMemo(() => monthsOf({ selectedJobData, completionDateForecast, jobCostDetail, userData, extraMonths, overrideMonth, currentMonth }),
        [selectedJobData, completionDateForecast, jobCostDetail, userData, extraMonths, overrideMonth, currentMonth]);

    const remainingMonths = useMemo(() => months.filter((m) => !isPast(m)), [months]);

    // Default horizon is Complete Date - Forecast plus 5 months, independent
    // of `months` above — that array is padded out further whenever real
    // data (saved spreads, cost history, burn weeks) already reaches past
    // this horizon, so totals never silently drop money sitting beyond the
    // visible window. "+ 5 mo" pushes this horizon (and nothing else) further.
    // A typed end month wins outright over the computed default.
    const horizonEnd = useMemo(() => horizonEndOf({ completionDateForecast, selectedJobData, extraMonths, overrideMonth, currentMonth }),
        [completionDateForecast, selectedJobData, extraMonths, overrideMonth, currentMonth]);

    const visibleMonths = useMemo(() => ([
        ...months.filter(isPast),
        ...remainingMonths.filter((m) => m.start.isSameOrBefore(horizonEnd)),
    ]), [months, remainingMonths, horizonEnd]);

    const hiddenMonths = useMemo(
        () => remainingMonths.filter((m) => m.start.isAfter(horizonEnd)),
        [remainingMonths, horizonEnd],
    );

    // Cost-detail rows scoped to the Vendor/Phase/Resource filter above the
    // table. Feeds the table's actuals and the month charts.
    const filteredCostDetail = useMemo(() => {
        if (!filterActive) return jobCostDetail;
        return (jobCostDetail || []).filter((r) => (
            (!filterVendor || rowVendor(r) === filterVendor) &&
            (!filterPhase || rowPhase(r) === filterPhase) &&
            (!filterResource || rowResource(r) === filterResource)
        ));
    }, [jobCostDetail, filterActive, filterVendor, filterPhase, filterResource]);

    // Revenue row — posted revenue by month. Cost-plus / T&M jobs post revenue
    // as GL lines (cost type 40000-49999) in Job Cost History; fixed-price jobs
    // don't, and recognise revenue only through AR billings. Read the GL lines
    // when the job has them, otherwise fall back to billed amount by GL month.
    const revenueMonthly = useMemo(() => revenueMonthlyFrom(filteredCostDetail, jobBillings, currentMonth), [filteredCostDetail, jobBillings, currentMonth]);

    const filterOptions = useMemo(() => {
        const curveRows = (jobCostDetail || []).filter((r) => inCurveRanges(r?.Cost_Type_Code != null ? String(r.Cost_Type_Code) : ''));
        // Each list reflects the other two selections so the choices stay valid.
        const uniq = (get, skip) => [...new Set(curveRows
            .filter((r) => (
                (skip === 'vendor' || !filterVendor || rowVendor(r) === filterVendor) &&
                (skip === 'phase' || !filterPhase || rowPhase(r) === filterPhase) &&
                (skip === 'resource' || !filterResource || rowResource(r) === filterResource)
            ))
            .map(get).filter(Boolean))].sort();
        return {
            vendors: uniq(rowVendor, 'vendor'),
            phases: uniq(rowPhase, 'phase'),
            resources: uniq(rowResource, 'resource'),
        };
    }, [jobCostDetail, filterVendor, filterPhase, filterResource]);

    // ---- Labor / Equipment monthly curve from the Burn Tool (helpers/revenueCurve) --
    // Runs on the filtered rows for the table and month charts.
    const burnMonthly = useMemo(() => burnMonthlyFrom(filteredCostDetail, userData, burnCutoff), [filteredCostDetail, userData, burnCutoff]);

    const actualsMonthly = useMemo(() => actualsMonthlyFrom(filteredCostDetail), [filteredCostDetail]);

    const savedByRowId = useMemo(() => savedByRowIdOf(userData), [userData]);

    const nodeIdsByBase = useMemo(() => nodeIdsByBaseOf(savedByRowId), [savedByRowId]);

    const savedOrEdited = (id) => (edits[id] !== undefined ? pNum(edits[id]) : (savedByRowId[id]?.amount || 0));

    // ETC held directly on the category row's own cell.
    const unallocatedEtc = (row, m) => savedOrEdited(cellId(m.key, row.costType));

    // Category ETC always counts any legacy drill-node rows saved under it, so
    // old data keeps counting toward the total even with no drill UI left to
    // show it broken out.
    const categoryEtc = (row, m) => {
        const base = `${m.key}*${row.costType}`;
        const known = nodeIdsByBase[base] || [];
        let sum = unallocatedEtc(row, m);
        known.forEach((id) => { sum += savedOrEdited(id); });
        Object.keys(edits).forEach((id) => {
            if (id.startsWith(base + '*') && !known.includes(id)) sum += pNum(edits[id]);
        });
        return sum;
    };

    // Every cell is read as two halves — cost already posted, and ETC still to
    // spend. Closed months are all actual and future months are all ETC, but
    // the month in progress carries both, so a cost posted this month has to
    // land in `actual` instead of being shadowed by the ETC typed over it.
    //
    // ETC is entered per month on this page and never filtered, so only the
    // actual and burn sources differ between the whole-job and filtered scopes.
    const readersFrom = (actualsSrc, burnSrc) => {
        const actual = (row, m) => (row.editable
            ? (actualsSrc[row.key] && actualsSrc[row.key][m.key]) || 0
            : (burnSrc[row.key] && burnSrc[row.key].actual[m.key]) || 0);
        const etc = (row, m) => {
            // Burn weeks are already split actual vs estimate at the week
            // level, so they need no second cutoff by month — and the cutoff
            // week can sit inside a month that has otherwise closed, which a
            // month-level test would drop on the floor.
            if (!row.editable) return (burnSrc[row.key] && burnSrc[row.key].est[m.key]) || 0;
            return isPast(m) ? 0 : categoryEtc(row, m);
        };
        return { actual, etc, value: (row, m) => actual(row, m) + etc(row, m) };
    };
    const R = readersFrom(actualsMonthly, burnMonthly);          // filtered
    const cellValue = R.value;

    // ETC already entered on the third-party pages. Read-only here: the Cost
    // Curve spreads these totals over months without ever writing back to
    // those tabs, so this page can't change what they report.
    const etcPoolByRow = useMemo(() => etcPoolByRowOf(userData), [userData]);

    const monthTotal = (m, r = R) => ROWS.reduce((s, row) => s + r.value(row, m), 0);

    // Only worth flagging hidden months that actually carry ETC — an empty
    // month past the window changes nothing the user can see.
    const hiddenEtc = hiddenMonths.reduce((s, m) => s + monthTotal(m), 0);

    // The job's own EAC margin — the same JP% the Overview tab's Job Profit
    // card shows, off the same sidemenu atoms so the two can't disagree.
    // Revenue is contract + approved COs; cost is the EAC across all six
    // buckets plus contingency.
    const jobProfitPct = useMemo(() => jobProfitPctOf(smBudgets, smCosts), [smBudgets, smCosts]);

    // Forecast cost earns revenue at the job's overall JP%: nothing is billed
    // for a future month yet, so the only defensible revenue for it is the
    // cost planned that month grossed up by the margin the job expects to
    // make. Revenue = Cost / (1 - JP%), which lands the month's Job Margin
    // back on exactly JP%.
    //
    // The month in progress (A+F) earns the same way. Its posted "revenue" is
    // raw billings — the 43000 accrual that would convert them to earned
    // revenue only books at month-end, after the version snapshot — so
    // blending posted + ETC at JP% covered the month's actual cost with
    // billings alone and produced a nonsense margin. Grossing the whole
    // month's cost at JP% keeps every open month on the job's own margin.
    const monthEtcTotal = (m, r = R) => ROWS.reduce((s, row) => s + r.etc(row, m), 0);
    const forecastRevenueForMonth = (m) => {
        if (isPast(m) || jobProfitPct == null) return 0;
        return monthTotal(m) / (1 - jobProfitPct);
    };
    // Closed months report the revenue that actually posted; the Forecast
    // Date's own month and every month after it earn their whole cost at JP%.
    const revenueForMonth = (m) => (isPast(m) || jobProfitPct == null
        ? (revenueMonthly[m.key] || 0)
        : forecastRevenueForMonth(m));

    // Job Profit / Job Margin rows — same JP = Revenue - Cost formula the
    // Overview tab's Job Profit card uses, just run per month instead of per
    // budget basis. Revenue is the Earned Revenue row below; cost is that month's EAC.
    const jobProfitForMonth = (m) => revenueForMonth(m) - monthTotal(m);
    // Margin divides by |revenue| so the sign always follows profit: a month
    // whose posted revenue nets negative (an O/U reversal outrunning the
    // accrual) would otherwise show a loss as a big positive percentage.
    const jobMarginForMonth = (m) => {
        const rev = revenueForMonth(m);
        return rev ? (jobProfitForMonth(m) / Math.abs(rev)) * 100 : 0;
    };
    // Negative reads as parens in red, matching the accounting convention
    // used everywhere else money can go negative on this page.
    // Export the month-by-month margin math to a formatted workbook: a rollup
    // sheet where Earned Revenue / Profit / Margin are live Excel formulas off
    // the JP% cell (so the calc can be audited and stress-tested), and a detail
    // sheet with each category's actual/ETC halves. Respects the active
    // Vendor/Phase/Resource filter — the same readers the table renders from.
    const exportExcel = async () => {
        const n = (v) => Math.round((v || 0) * 100) / 100;
        const MONEY = '$#,##0;[Red]($#,##0)';
        const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
        const AF_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
        const TOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
        const exportMonths = months.filter((m) => (
            Math.abs(monthTotal(m)) > 0.005 || Math.abs(revenueForMonth(m)) > 0.005 || Math.abs(revenueMonthly[m.key] || 0) > 0.005
        ));
        const tagOf = (m) => (isPast(m) ? 'A' : isCurrent(m) ? 'A+F' : 'F');
        const filterLabel = filterActive
            ? [filterVendor && `Vendor=${filterVendor}`, filterPhase && `Phase=${filterPhase}`, filterResource && `Resource=${filterResource}`].filter(Boolean).join('; ')
            : 'none';

        const workbook = new ExcelJS.Workbook();

        // ---- Sheet 1: rollup with live formulas ----------------------------
        const roll = workbook.addWorksheet('Margin Rollup', { views: [{ state: 'frozen', ySplit: 9 }] });
        roll.columns = [
            { width: 12 }, { width: 7 }, { width: 15 }, { width: 15 }, { width: 15 },
            { width: 16 }, { width: 16 }, { width: 15 }, { width: 13 },
        ];
        roll.getCell('A1').value = 'REVENUE CURVE — MARGIN ROLLUP';
        roll.getCell('A1').font = { bold: true, size: 14 };
        const metaRows = [
            ['Job', `${selectedJob}${selectedJobData?.BU_Description ? ' — ' + selectedJobData.BU_Description : ''}`],
            ['Version', `${selectedVersion} (Forecast Date ${forecastDate.format('MM/DD/YYYY')})`],
            ['A+F Month', currentMonth.format('MMM YYYY')],
            ['Job JP%', jobProfitPct == null ? 'n/a — no contract revenue (or margin ≥ 100%), so forecast months earn $0' : jobProfitPct],
            ['Filter', filterLabel],
        ];
        metaRows.forEach(([k, v], i) => {
            const row = roll.getRow(2 + i);
            row.getCell(1).value = k;
            row.getCell(1).font = { bold: true, color: { argb: 'FF64748B' } };
            row.getCell(2).value = v;
        });
        // The JP% cell every formula below points at.
        const JP = '$B$5';
        if (jobProfitPct != null) roll.getCell('B5').numFmt = '0.00%';
        roll.getCell('B5').font = { bold: true };
        roll.mergeCells('A7:I7');
        roll.getCell('A7').value =
            'Earned Revenue — A: posted revenue. A+F and F: total cost ÷ (1 − JP%) — the open month’s billings aren’t earned revenue until the month-end O/U accrual books. Change B5 to stress-test the margin.';
        roll.getCell('A7').font = { italic: true, size: 9, color: { argb: 'FF94A3B8' } };
        roll.getCell('A7').alignment = { wrapText: true };
        roll.getRow(7).height = 26;

        const rollHeader = roll.getRow(9);
        ['Month', 'Tag', 'Total Actual', 'Total ETC', 'Total Cost', 'Posted Revenue', 'Earned Revenue', 'Job Profit', 'Job Margin %']
            .forEach((h, i) => { rollHeader.getCell(i + 1).value = h; });
        rollHeader.font = { bold: true };
        rollHeader.eachCell((c) => { c.fill = HEADER_FILL; });

        const firstData = 10;
        exportMonths.forEach((m, i) => {
            const r = firstData + i;
            const tag = tagOf(m);
            const totalActual = ROWS.reduce((s, row) => s + R.actual(row, m), 0);
            const row = roll.getRow(r);
            row.getCell(1).value = m.start.format("MMM 'YY");
            row.getCell(2).value = tag;
            row.getCell(2).alignment = { horizontal: 'center' };
            if (tag === 'A+F') row.getCell(2).fill = AF_FILL;
            row.getCell(3).value = n(totalActual);
            row.getCell(4).value = n(monthEtcTotal(m));
            row.getCell(5).value = { formula: `C${r}+D${r}` };
            row.getCell(6).value = n(revenueMonthly[m.key] || 0);
            row.getCell(7).value = {
                formula: jobProfitPct == null || tag === 'A' ? `F${r}`
                    : `E${r}/(1-${JP})`,
            };
            // Negative earned revenue (unmatched O/U reversal) makes profit
            // and margin meaningless for the month — blank both, same as the
            // on-screen table.
            row.getCell(8).value = { formula: `IF(G${r}<0,"",G${r}-E${r})` };
            row.getCell(9).value = { formula: `IF(G${r}<=0,"",(G${r}-E${r})/ABS(G${r}))` };
            row.getCell(9).numFmt = '0.00%;[Red](0.00%)';
            for (let c = 3; c <= 8; c++) row.getCell(c).numFmt = MONEY;
        });
        const lastData = firstData + exportMonths.length - 1;
        const totalRow = roll.getRow(lastData + 1);
        totalRow.getCell(1).value = 'TOTAL';
        for (let c = 3; c <= 7; c++) {
            totalRow.getCell(c).value = { formula: `SUM(${'ABCDEFG'[c - 1]}${firstData}:${'ABCDEFG'[c - 1]}${lastData})` };
            totalRow.getCell(c).numFmt = MONEY;
        }
        // Job-total profit comes off total revenue and cost, not the month
        // cells — hidden (blanked) months still count toward the job.
        const tr = lastData + 1;
        totalRow.getCell(8).value = { formula: `G${tr}-E${tr}` };
        totalRow.getCell(8).numFmt = MONEY;
        totalRow.getCell(9).value = { formula: `IF(G${tr}=0,"",H${tr}/ABS(G${tr}))` };
        totalRow.getCell(9).numFmt = '0.00%;[Red](0.00%)';
        totalRow.font = { bold: true };
        totalRow.eachCell({ includeEmpty: false }, (c) => { c.fill = TOTAL_FILL; });

        // ---- Sheet 2: category detail --------------------------------------
        const det = workbook.addWorksheet('Category Detail', { views: [{ state: 'frozen', xSplit: 2, ySplit: 3 }] });
        det.getCell('A1').value = `Category actual / ETC by month — job ${selectedJob}, v${selectedVersion}` + (filterActive ? ` (filtered: ${filterLabel})` : '');
        det.getCell('A1').font = { bold: true, size: 12 };
        const detHeader = det.getRow(3);
        const detCols = ['Month', 'Tag', ...ROWS.flatMap((row) => [`${row.label} Actual`, `${row.label} ETC`]), 'Total Actual', 'Total ETC', 'Total Cost'];
        detCols.forEach((h, i) => { detHeader.getCell(i + 1).value = h; });
        detHeader.font = { bold: true };
        detHeader.eachCell((c) => { c.fill = HEADER_FILL; c.alignment = { wrapText: true, vertical: 'bottom' }; });
        det.columns = detCols.map((_, i) => ({ width: i === 0 ? 12 : i === 1 ? 7 : 13 }));

        const colLetter = (i) => det.getColumn(i).letter;   // 1-based
        const nCats = ROWS.length;
        const actualCols = ROWS.map((_, i) => colLetter(3 + i * 2));
        const etcCols = ROWS.map((_, i) => colLetter(4 + i * 2));
        const tActualCol = 3 + nCats * 2, tEtcCol = tActualCol + 1, tCostCol = tActualCol + 2;
        exportMonths.forEach((m, i) => {
            const r = 4 + i;
            const row = det.getRow(r);
            row.getCell(1).value = m.start.format("MMM 'YY");
            row.getCell(2).value = tagOf(m);
            row.getCell(2).alignment = { horizontal: 'center' };
            if (tagOf(m) === 'A+F') row.getCell(2).fill = AF_FILL;
            ROWS.forEach((cat, j) => {
                row.getCell(3 + j * 2).value = n(R.actual(cat, m));
                row.getCell(4 + j * 2).value = n(R.etc(cat, m));
            });
            row.getCell(tActualCol).value = { formula: actualCols.map((L) => `${L}${r}`).join('+') };
            row.getCell(tEtcCol).value = { formula: etcCols.map((L) => `${L}${r}`).join('+') };
            row.getCell(tCostCol).value = { formula: `${colLetter(tActualCol)}${r}+${colLetter(tEtcCol)}${r}` };
            for (let c = 3; c <= tCostCol; c++) row.getCell(c).numFmt = MONEY;
        });
        const detLast = 3 + exportMonths.length;
        const detTotal = det.getRow(detLast + 1);
        detTotal.getCell(1).value = 'TOTAL';
        for (let c = 3; c <= tCostCol; c++) {
            detTotal.getCell(c).value = { formula: `SUM(${colLetter(c)}4:${colLetter(c)}${detLast})` };
            detTotal.getCell(c).numFmt = MONEY;
        }
        detTotal.font = { bold: true };
        detTotal.eachCell({ includeEmpty: false }, (c) => { c.fill = TOTAL_FILL; });

        // ---- Sheet 3: flat, filterable line-level rows ---------------------
        // One row per underlying line across the three sources the curve is
        // built from — GL actuals, forecast entries (curve ETC cells + Burn
        // Tool weeks), and open PO commitments — so the whole thing can be
        // sliced with Excel's own filters. Raw source rows, deliberately NOT
        // the curve's derived actual/est split: every line traces back to
        // where it was entered or posted.
        const all = workbook.addWorksheet('All Rows', { views: [{ state: 'frozen', ySplit: 1 }] });
        const allCols = [
            { header: 'Source', width: 22 }, { header: 'Category', width: 22 },
            { header: 'Month', width: 10 }, { header: 'Tag', width: 6 },
            { header: 'Date', width: 12 }, { header: 'Cost Type', width: 10 },
            { header: 'Cost Type Desc', width: 22 }, { header: 'Phase', width: 10 },
            { header: 'Phase Desc', width: 24 }, { header: 'Vendor', width: 26 },
            { header: 'PO', width: 12 }, { header: 'Resource', width: 18 },
            { header: 'Description', width: 34 }, { header: 'Hours', width: 9 },
            { header: 'Amount', width: 14 },
        ];
        all.columns = allCols;
        all.getRow(1).font = { bold: true };
        all.getRow(1).eachCell((c) => { c.fill = HEADER_FILL; });
        all.autoFilter = { from: 'A1', to: { row: 1, column: allCols.length } };

        const catFor = (ct) => {
            if (ct >= REVENUE_RANGE[0] && ct <= REVENUE_RANGE[1]) return 'Revenue';
            const hit = ROWS.find((r) => { const [lo, hi] = rangeFor(r); return ct >= lo && ct <= hi; });
            return hit ? hit.label : (ct ? 'Out of Curve' : '');
        };
        const tagForMk = (mk) => {
            if (!/^\d{4}-\d{2}$/.test(mk || '')) return '';
            const m = moment(mk + '-01');
            return m.isBefore(currentMonth) ? 'A' : m.isSame(currentMonth) ? 'A+F' : 'F';
        };
        const addAllRow = (vals) => {
            const row = all.addRow(vals);
            row.getCell(15).numFmt = MONEY;
            return row;
        };

        // GL actuals — same rows the table's actual halves read (respects the
        // filter). Revenue lines post negative; flipped so the Revenue
        // category filters to positive dollars.
        (filteredCostDetail || []).forEach((r) => {
            const ct = r?.Cost_Type_Code != null ? String(r.Cost_Type_Code) : '';
            const isRev = ct >= REVENUE_RANGE[0] && ct <= REVENUE_RANGE[1];
            const mk = moment(r.Consolidated_Date).add(12, 'hours').format('YYYY-MM');
            addAllRow([
                'Actual (GL)', catFor(ct), mk, tagForMk(mk),
                r.Consolidated_Date ? moment(r.Consolidated_Date).add(12, 'hours').format('MM/DD/YYYY') : '',
                ct, r.Cost_Type_Code_Description || '', rowPhase(r), r.Phase_Code_Description || '',
                rowVendor(r), r.PO_Number || '', rowResource(r),
                r.Transaction_Description || r.Remark_Description || '',
                pNum(r.Total_Hours) || '', isRev ? -(Number(r.Job_Cost) || 0) : (Number(r.Job_Cost) || 0),
            ]);
        });

        // Curve ETC cells — the spreads typed on this page (month*costType).
        Object.entries(savedByRowId).forEach(([id, d]) => {
            const p = id.split('*');
            if (!/^\d{4}-\d{2}$/.test(p[0]) || !d.amount) return;
            const rowDef = ROWS.find((r) => r.costType === p[1]);
            addAllRow([
                'Forecast — Curve ETC', rowDef ? rowDef.label : catFor(p[1]), p[0], tagForMk(p[0]),
                '', p[1], '', '', '', '', '', '', p.length > 2 ? `legacy drill node ${p.slice(2).join('*')}` : '', '', d.amount,
            ]);
        });

        // Burn Tool weeks — raw week estimates (phased rows carry their phase
        // code in the id). Week dollars are shown on the week's own month here;
        // the curve itself spreads a month-crossing week across both months.
        Object.entries(BURN_TABS).forEach(([key, tabs]) => {
            const label = key === 'labor' ? 'Labor' : 'Equipment';
            const byId = {};
            (userData || []).forEach((d) => {
                if (d?.Forecast_Tab === tabs.phased || d?.Forecast_Tab === tabs.regular) byId[d.Forecast_Row_ID] = d;
            });
            Object.entries(byId).forEach(([id, d]) => {
                const wk = burnRowWeek(id);
                const est = burnRowEst(d);
                if (!wk || !est) return;
                const mk = moment(wk).format('YYYY-MM');
                const phase = id.includes('=') ? id.split('=')[0] : '';
                addAllRow([
                    `Forecast — Burn Tool ${label}`, label, mk, tagForMk(mk),
                    moment(wk).format('MM/DD/YYYY'), '', '', phase, phase ? (jobPhaseCodes?.[phase] || '') : '',
                    '', '', '', `week of ${moment(wk).format('MM/DD')} — ${pNum(d.Number_Units) || 0} × ${pNum(d.Number_Hours) || 0} hrs × $${pNum(d.Rate) || 0}`,
                    (pNum(d.Rate) > 0 && d.Number_Hours ? pNum(d.Number_Hours) * pNum(d.Number_Units) : '') || '', est,
                ]);
            });
        });

        // Open PO commitments — the true open amount still to be received.
        (jobOpenCommitments || []).forEach((r) => {
            const ct = r?.Cost_Type_Code != null ? String(r.Cost_Type_Code) : '';
            const open = pNum(r.PO_True_Open_Amount);
            if (!open) return;
            const dt = r.GL_Date || r.Order_Date;
            const mk = dt ? moment(dt).add(12, 'hours').format('YYYY-MM') : '';
            addAllRow([
                'Committed (Open PO)', catFor(ct), mk, tagForMk(mk),
                dt ? moment(dt).add(12, 'hours').format('MM/DD/YYYY') : '',
                ct, r.Cost_Type_Description || '', r.Phase_Code || '', r.Phase_Code_Description || '',
                r.Supplier_AB_Name || '', r.PO_Number || '',
                r.Purchasing_Reporting_Code_4 || r.Purchasing_Reporting_Code_1 || r.Equipment_Classification_Tier_Name || '',
                [r.PO_Line_Description, r.PO_Line_Description_2].filter(Boolean).join(' '),
                '', open,
            ]);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Revenue_Curve_${selectedJob}_v${selectedVersion}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
    };

    const profitDisplay = (v) => (v < 0 ? '(' + fNum(Math.abs(v), 1) + ')' : $(v));
    const marginDisplay = (v) => `${v < 0 ? '(' : ''}${Math.abs(v).toFixed(2)}%${v < 0 ? ')' : ''}`;
    const profitColor = (v) => (v < 0 ? '#EF4444' : '#0F172A');

    // ---- Data date (latest closed cost date) -----------------------------
    const dataDate = useMemo(() => {
        let max;
        (jobCostDetail || []).forEach((r) => {
            if (!r?.Consolidated_Date) return;
            const d = moment(r.Consolidated_Date);
            if (d.isValid() && (!max || d.isAfter(max))) max = d.clone();
        });
        return (max || moment()).format('MM/DD/YYYY');
    }, [jobCostDetail]);

    // ---- Persistence -----------------------------------------------------
    const buildPayload = (rowId, costType, amount, fieldName = 'row') => {
        const knownGuid = savedByRowId[rowId]?.guid;
        return {
            active: 1,
            userId: userId || '', userName: userName || '', userEmail: userEmail || '',
            fieldName,
            tab: TAB,
            rowId,
            version: selectedVersion + '',
            value: amount + '',
            valueAmount: amount,
            id: selectedJob,
            costType,
            lockedVersion: versionLocked,
            ...(knownGuid ? { updateRow: 1, guidId: knownGuid + '' } : {}),
        };
    };
    const stateRowFor = (rowId, costType, amount, fieldName, guid = '') => ({
        Forecast_Field_Name: fieldName,
        Forecast_Tab: TAB,
        Forecast_Row_ID: rowId,
        Forecast_User_Data_GUID: guid,
        BU_ID: selectedJob,
        Version_GUID: selectedVersion,
        Cost_Type: costType,
        Value_Amount: amount,
    });

    const commitRows = (items) => {
        if (!items.length) return;
        const payloads = items.map((c) => buildPayload(c.rowId, c.costType, c.amount, c.fieldName || 'row'));

        setUserData((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            items.forEach((c) => {
                const keptGuid = savedByRowId[c.rowId]?.guid || '';
                const idx = list.findIndex((d) => d.Forecast_Tab === TAB && d.Forecast_Row_ID === c.rowId);
                const row = stateRowFor(c.rowId, c.costType, c.amount, c.fieldName || 'row', keptGuid);
                if (idx >= 0) list[idx] = { ...list[idx], ...row };
                else list.push(row);
            });
            return list;
        });

        updateUserDataArray(payloads).then((guids) => {
            if (!Array.isArray(guids)) return;
            setUserData((prev) => {
                if (!Array.isArray(prev)) return prev;
                const list = [...prev];
                items.forEach((c, i) => {
                    const g = guids[i];
                    if (!g) return;
                    const idx = list.findIndex((d) => d.Forecast_Tab === TAB && d.Forecast_Row_ID === c.rowId && !d.Forecast_User_Data_GUID);
                    if (idx >= 0) list[idx] = { ...list[idx], Forecast_User_Data_GUID: g };
                });
                return list;
            });
        });
    };

    // Persist the window's end month (or clear it with null). Rides the same
    // commitRows path as the ETC cells, so the optimistic userData update
    // moves the window immediately and the PSR slide reads the saved value.
    const saveHorizon = (m) => {
        if (versionLocked) return;
        const amount = m && m.isValid() ? m.year() * 100 + (m.month() + 1) : 0;
        commitRows([{ rowId: HORIZON_ROW_ID, costType: '', amount, fieldName: 'horizonEnd' }]);
    };

    // The ± 5 mo buttons move whichever horizon is live: a saved end month
    // shifts directly, otherwise the default horizon's padding grows/shrinks.
    const shiftHorizon = (n) => {
        if (overrideMonth) saveHorizon(overrideMonth.clone().add(n, 'months'));
        else setExtraMonths((x) => Math.max(0, x + n));
    };

    // What a category still has left to spread, given its ledger pool and the
    // months already filled in. `exceptId` is the cell being edited, so its own
    // current value doesn't count against the headroom.
    // Counts saved values only — never what is being typed — so the headroom on
    // screen moves when a cell is committed, not on every keystroke.
    const poolRemaining = (row, exceptId) => {
        const pool = etcPoolByRow[row.key];
        if (pool == null) return null;
        const saved = (id) => (id === exceptId ? 0 : (savedByRowId[id]?.amount || 0));
        const spread = remainingMonths.reduce((sum, m) => {
            const base = cellId(m.key, row.costType);
            let cell = saved(base);
            (nodeIdsByBase[base] || []).forEach((id) => { cell += saved(id); });
            return sum + cell;
        }, 0);
        return pool - spread;
    };

    // Blur and Enter both land here. Untouched cells carry no edit buffer, so
    // clicking through a row commits nothing and can't zero anything out.
    const commitEdit = (m, costType, id) => {
        if (edits[id] === undefined) return;
        commitCell(m, costType, edits[id]);
    };

    const commitCell = (m, costType, raw) => {
        const id = cellId(m.key, costType);
        const row = ROWS.find((r) => r.costType === costType);
        const amount = pNum(raw);
        const headroom = row ? poolRemaining(row, id) : null;
        const current = savedByRowId[id]?.amount || 0;

        // The pool is what the third-party pages already committed to; this page
        // only decides when it lands, so it can never total to more. Only an
        // increase can breach it — clearing or lowering a cell always reduces the
        // total, so those go through even when the pool is already overrun.
        if (amount > current && headroom != null && amount > headroom) {
            // Anchor the popup to the cell that was rejected. Fixed positioning
            // keeps it clear of the table's horizontal scroll container.
            const el = typeof document !== 'undefined' ? document.querySelector(`[data-cell-id="${id}"]`) : null;
            const r = el?.getBoundingClientRect?.();
            setPoolWarning({
                key: row.key, tried: amount, left: headroom, id,
                at: r ? { top: r.bottom + 6, left: r.left } : null,
            });
            setEdits((e) => { const n = { ...e }; delete n[id]; return n; });
            return;
        }
        setPoolWarning(null);
        commitRows([{ rowId: id, costType, amount }]);
        setEdits((e) => { const n = { ...e }; delete n[id]; return n; });
    };

    // Clears the category row and every drill node saved beneath it, so a
    // cleared row really is zero rather than zero plus hidden children.
    const clearRow = (row) => {
        const ids = [];
        remainingMonths.forEach((m) => {
            const base = cellId(m.key, row.costType);
            ids.push(base);
            (nodeIdsByBase[base] || []).forEach((id) => ids.push(id));
        });
        commitRows(ids.map((rowId) => ({ rowId, costType: row.costType, amount: 0 })));
        setEdits((e) => {
            const n = { ...e };
            ids.forEach((id) => delete n[id]);
            return n;
        });
    };

    // Months where something has actually been applied — actual cost, ETC, or
    // revenue — as opposed to the padded horizon (which runs 5+ months past
    // the last real entry, further still after "+ 5 mo").
    const appliedMonths = visibleMonths.filter((m) => revenueForMonth(m) !== 0 || monthTotal(m) !== 0);

    // The PSR slide has no room for a full-width table, so its copy of the
    // grid drops any month column with nothing posted or forecast in it
    // (padding months at either edge of the horizon) rather than shrinking
    // every column to fit.
    const photoVisibleMonths = a?.photo ? appliedMonths : visibleMonths;

    // The aspect-matched width above assumes the table is the narrower of the
    // two stacked blocks. A job with many active months breaks that: the grid
    // (fixed label/remaining columns + 128px per month) can come out wider
    // than the aspect width, and since the table panel is centered inside the
    // capped chartsRef view, the excess overflows both edges and the slide
    // capture clips it. Floor the composite at the table's own width so the
    // capture always contains the whole grid; the image just runs wider than
    // the slide box's aspect and gets fit by width instead.
    const photoTableWidth = 150 + 170 + photoVisibleMonths.length * 128 + 30; // label + remaining + months + panel padding/border
    const photoCompositeWidth = Math.max(photoAspectWidth, a?.photo ? photoTableWidth : 0);

    // Shared between the interactive tab (full visibleMonths) and the PSR
    // slide capture (photoVisibleMonths) so the two never drift apart.
    const renderInputGrid = (tableMonths) => (
        <View style={{ width: 150 + 170 + tableMonths.length * 128 }}>
            <View style={s.gridRow}>
                <View style={[s.headCell, s.labelCol]}><Text style={[s.headText, s.headTextLeft]}>Cost Category</Text></View>
                <View style={[s.headCell, s.remainingCol]} title="ETC still to spread across the forecast months"><Text style={[s.headText, s.headTextLeft]}>{a?.photo ? 'Total Cost' : 'Remaining Cost'}</Text></View>
                {tableMonths.map((m) => {
                    const past = isPast(m);
                    const now = isCurrent(m);
                    return (
                        <View key={m.key} style={[s.headCell, s.monthCol, past && s.pastHead]}>
                            <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 4 }, s.headTextLeft]}>
                                <Text style={[s.monthTag, past ? s.monthTagA : now ? s.monthTagAF : s.monthTagF]}
                                    title={past
                                        ? 'Actual — closed month'
                                        : now
                                            ? 'Month in progress — cost posted so far plus the ETC you type for the rest of it'
                                            : 'Forecast — open month'}>
                                    {past ? 'A' : now ? 'A+F' : 'F'}
                                </Text>
                                <Text style={[s.headText, { whiteSpace: 'nowrap' }]}>{m.label}</Text>
                            </View>
                        </View>
                    );
                })}
            </View>

            {ROWS.map((row) => {
              return (
                <View key={row.key} style={s.gridRow}>
                    <View style={[s.cell, s.labelCol, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }]}>
                        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: row.color, marginRight: 6 }} />
                        <Text style={s.labelText}>{a?.photo && row.key === 'rentals' ? 'Rntl/Fuel/Rep' : row.label}</Text>
                    </View>

                    <View style={[s.cell, s.remainingCol, s.remainingCellRow, !a?.photo && etcPoolByRow[row.key] != null && (Math.round(poolRemaining(row)) > 0 ? s.remainingOver : s.remainingClear)]}>
                        {(() => {
                            const pool = etcPoolByRow[row.key];
                            const left = pool == null ? null : poolRemaining(row);
                            return (
                                <>
                                    {pool == null
                                        ? <Text style={[s.numText, { color: '#CBD5E1' }]}>—</Text>
                                        : (
                                            <Text style={[s.numText, { fontWeight: '600' }]} title={`${$(left)} of ${$(pool)} total ETC still to spread`}>
                                                {$(left)}
                                            </Text>
                                        )}
                                    {row.editable ? (
                                        <Pressable style={s.clearBtn} disabled={versionLocked} onPress={() => clearRow(row)}>
                                            <Text style={s.clearBtnText}>Clear</Text>
                                        </Pressable>
                                    ) : null}
                                </>
                            );
                        })()}
                    </View>

                    {tableMonths.map((m) => {
                        const v = cellValue(row, m);
                        const locked = !row.editable || isPast(m);
                        if (locked) {
                            return (
                                <View key={m.key} style={[s.cell, s.monthCol, s.readonlyCell]}
                                    title={isPast(m) ? 'Actual (historical)' : 'Set in the Burn Tool'}>
                                    <Text style={[s.numText, { color: '#94A3B8' }]}>{v ? $(v) : '—'}</Text>
                                </View>
                            );
                        }
                        const id = cellId(m.key, row.costType);
                        // The box edits ETC only. In the month in progress the cost
                        // already posted sits under it, so the cell still shows the
                        // full month while the input stays typeable.
                        const etcOnly = R.etc(row, m);
                        const posted = R.actual(row, m);
                        const display = edits[id] !== undefined ? edits[id] : (etcOnly ? fNum(etcOnly, 1) : '');
                        const input = (
                            <TextInput
                                style={[posted ? s.splitInput : s.cellInput, poolWarning?.id === id && s.cellInputBad]}
                                data-cell-id={id}
                                value={display}
                                editable={!versionLocked}
                                placeholder="—"
                                onFocus={() => { if (poolWarning?.id === id) setPoolWarning(null); }}
                                onChangeText={(t) => setEdits((e) => ({ ...e, [id]: t }))}
                                onSubmitEditing={() => commitEdit(m, row.costType, id)}
                                onBlur={() => commitEdit(m, row.costType, id)}
                            />
                        );
                        // A month carrying both actual and forecast splits into two equal
                        // halves, each tagged the same way the column header tags months —
                        // F on top for what you're typing, A below for what already posted.
                        return (
                            <View key={m.key} style={[s.cell, s.monthCol, { padding: 0 }]}>
                                {posted ? (
                                    <>
                                        <View style={s.splitHalf}>
                                            <Text style={[s.monthTag, s.monthTagF, s.splitTag]}>F</Text>
                                            {input}
                                        </View>
                                        <View style={[s.splitHalf, s.splitHalfBottom]}>
                                            <Text style={[s.monthTag, s.monthTagA, s.splitTag]}>A</Text>
                                            <Text style={s.splitActualValue}
                                                title={`${$(posted)} already posted in ${m.label} — counted as spent, not ETC`}>
                                                {$(posted)}
                                            </Text>
                                        </View>
                                    </>
                                ) : input}
                            </View>
                        );
                    })}
                </View>
              );
            })}

            <View style={[s.gridRow, s.totalRow]}>
                <View style={[s.cell, s.labelCol, { justifyContent: 'center', alignItems: 'flex-start' }]}><Text style={[s.labelText, { fontWeight: '700' }]}>TOTAL</Text></View>
                <View style={[s.cell, s.remainingCol]}>
                    <Text style={[s.numText, { fontWeight: '700' }]}>
                        {$(ROWS.reduce((sum, r) => sum + (poolRemaining(r) || 0), 0))}
                    </Text>
                </View>
                {tableMonths.map((m) => (
                    <View key={m.key} style={[s.cell, s.monthCol]}><Text style={[s.numText, { fontWeight: '700' }]}>{$(monthTotal(m))}</Text></View>
                ))}
            </View>

            <View style={s.gridRow}>
                <View style={[s.cell, s.labelCol, s.jpLabelCell]}><Text style={[s.labelText, { fontWeight: '700' }]}>Earned Revenue</Text></View>
                <View style={[s.cell, s.remainingCol]}><Text style={[s.numText, { color: '#CBD5E1' }]}>—</Text></View>
                {tableMonths.map((m) => {
                    const v = revenueForMonth(m);
                    const derived = forecastRevenueForMonth(m);
                    return (
                        <View key={m.key} style={[s.cell, s.monthCol]}
                            title={derived
                                ? `${$(monthTotal(m))} of cost this month earned at the job's ${(jobProfitPct * 100).toFixed(2)}% JP${isCurrent(m) ? " — billings posted so far aren't earned until the month-end O/U accrual books" : ''}`
                                : 'Revenue posted this month — GL revenue lines, or AR billings by GL date for jobs with none'}>
                            <Text style={[s.numText, { fontWeight: '600' }]}>{v ? $(v) : '—'}</Text>
                        </View>
                    );
                })}
            </View>

            <View style={s.gridRow}>
                <View style={[s.cell, s.labelCol, s.jpLabelCell]}><Text style={[s.labelText, { fontWeight: '700' }]}>Job Profit</Text></View>
                <View style={[s.cell, s.remainingCol]}><Text style={[s.numText, { color: '#CBD5E1' }]}>—</Text></View>
                {tableMonths.map((m) => {
                    const v = jobProfitForMonth(m);
                    // A month whose revenue nets negative (an O/U reversal
                    // outrunning the accrual) has no meaningful profit or
                    // margin — the billing just hasn't caught up — so both
                    // rows show a dash instead of an alarming number.
                    const hide = revenueForMonth(m) < 0;
                    return (
                        <View key={m.key} style={[s.cell, s.monthCol]}
                            title={hide
                                ? 'Posted revenue nets negative this month (O/U Billings reversal ahead of the accrual) — profit and margin are hidden until billing catches up'
                                : "Revenue for the month minus that month's total EAC"}>
                            <Text style={[s.numText, { fontWeight: '600', color: hide ? '#CBD5E1' : profitColor(v) }]}>{hide ? '—' : profitDisplay(v)}</Text>
                        </View>
                    );
                })}
            </View>

            <View style={s.gridRow}>
                <View style={[s.cell, s.labelCol, s.jpLabelCell]}><Text style={[s.labelText, { fontWeight: '700' }]}>Job Margin</Text></View>
                <View style={[s.cell, s.remainingCol]}><Text style={[s.numText, { color: '#CBD5E1' }]}>—</Text></View>
                {tableMonths.map((m) => {
                    const v = jobMarginForMonth(m);
                    const hide = revenueForMonth(m) < 0;
                    return (
                        <View key={m.key} style={[s.cell, s.monthCol]}
                            title={hide
                                ? 'Posted revenue nets negative this month (O/U Billings reversal ahead of the accrual) — profit and margin are hidden until billing catches up'
                                : isPast(m) || jobProfitPct == null
                                    ? "Job Profit for the month as a % of that month's Revenue"
                                    : `This month's cost earns at the job's total JP of ${(jobProfitPct * 100).toFixed(2)}%`}>
                            <Text style={[s.numText, { fontWeight: '600', color: hide ? '#CBD5E1' : profitColor(v) }]}>{hide ? '—' : marginDisplay(v)}</Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );

    // ---- Charts ----------------------------------------------------------
    // Charts only cover months where something has actually been applied in
    // the input table, rather than the whole padded horizon (which runs 5+
    // months past the last real entry, and further still each time "+ 5 mo"
    // is clicked) — otherwise the tail end of both charts is a stretch of
    // empty $0 columns.
    const chartMonths = appliedMonths;
    const monthLabels = chartMonths.map((m) => m.label);
    const barData = {
        labels: monthLabels,
        // Two datasets per row, adjacent in the stack and both on the same
        // stack id: each row's actual (posted, full color) segment sits
        // directly below its own forecast (ETC, low alpha) segment, then the
        // next row's pair stacks above that — actual and forecast for one
        // category stay together rather than every category's actual piling
        // up first. Past months carry all-actual, future months all-
        // forecast, and the current month splits between the two, matching
        // the posted-vs-ETC split the table's month cells already show.
        datasets: ROWS.flatMap((r) => ([
            {
                label: r.label,
                data: chartMonths.map((m) => Math.round(R.actual(r, m))),
                backgroundColor: r.color, stack: 'rev', borderWidth: 0,
            },
            {
                label: r.label,
                data: chartMonths.map((m) => Math.round(R.etc(r, m))),
                backgroundColor: r.color + '66', stack: 'rev', borderWidth: 0,
            },
        ])),
    };
    // The PSR slide shrinks these charts into a photo, so its axis labels and
    // legend need to start out larger than the interactive tab's or they read
    // as illegible once composited into the slide.
    const axisFontSize = a?.photo ? 16 : 9;
    const legendFontSize = a?.photo ? 16 : 10;
    const barOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom', labels: {
                    boxWidth: 10, font: { size: legendFontSize },
                    // The forecast half of each pair repeats the same label —
                    // keep only the actual-half (even-indexed) entries so the
                    // legend still shows one swatch per category, not twelve.
                    filter: (item) => item.datasetIndex % 2 === 0,
                },
            },
        },
        scales: {
            x: { stacked: true, grid: { display: false }, ticks: { font: { size: axisFontSize } } },
            y: { stacked: true, ticks: { font: { size: axisFontSize }, callback: (v) => '$' + (v / 1000000).toFixed(1) + 'M' } },
        },
    };
    // Cumulative total line laid over the stacked area, matching the mockup's
    // "Total EAC" trace.
    const cumData = {
        labels: monthLabels,
        datasets: [
            (() => {
                // Cumulative running sum over time, same pattern the ROWS
                // areas below use — no actual/forecast color split on this
                // chart (that distinction stays on the bar chart only).
                let run = 0;
                const data = chartMonths.map((m) => { run += revenueForMonth(m); return Math.round(run); });
                return {
                    label: 'Revenue', data,
                    borderColor: REVENUE_COLOR, backgroundColor: REVENUE_COLOR + 'B3',
                    // Filled like the ROWS areas, but on its own stack (not
                    // summed with 'cum') and the highest order so it draws
                    // last, on top of the cost stack and the EAC line.
                    fill: 'origin', stack: 'revenueLine', pointRadius: 2, pointBackgroundColor: REVENUE_COLOR,
                    borderWidth: 3, tension: 0.3, order: 3,
                };
            })(),
            ...ROWS.map((r, i) => {
                let run = 0;
                const data = chartMonths.map((m) => { run += cellValue(r, m); return Math.round(run); });
                return {
                    label: r.label, data,
                    borderColor: r.color, backgroundColor: r.color + 'B3',
                    fill: i === 0 ? 'origin' : '-1', stack: 'cum',
                    pointRadius: 0, borderWidth: 1, tension: 0.3, order: 1,
                };
            }),
            (() => {
                let run = 0;
                const data = chartMonths.map((m) => { run += monthTotal(m); return Math.round(run); });
                return {
                    label: 'Total EAC', data,
                    borderColor: TOTAL_EAC_COLOR, backgroundColor: TOTAL_EAC_COLOR,
                    // Own stack id: with the y-axis stacked, a line dataset with
                    // no stack id defaults to grouping by type, which sums it
                    // with any other unstacked line (Revenue) instead of
                    // plotting its raw value. Highest order so it draws last,
                    // on top of the cost stack, staying a crisp overlay line.
                    fill: false, stack: 'eacLine', pointRadius: 2, pointBackgroundColor: TOTAL_EAC_COLOR,
                    borderWidth: 2, tension: 0.3, order: 2,
                };
            })(),
        ],
    };
    const cumOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: legendFontSize } } } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: axisFontSize } } },
            y: { stacked: true, ticks: { font: { size: axisFontSize }, callback: (v) => '$' + (v / 1000000).toFixed(1) + 'M' } },
        },
    };

    // ---- Render ----------------------------------------------------------
    if (!selectedJob) {
        return <View style={s.empty}><Text style={s.emptyText}>Select a project to build its revenue curve forecast.</Text></View>;
    }

    const projName = selectedJobData?.Job_Name || selectedJobData?.BU_Name || `Project ${selectedJob}`;

    return (
        <View style={{ padding: 16 }}>
            {a?.photo ? null : (
            <>
            {/* Header */}
            <View style={s.headerRow}>
                <View>
                    <Text style={s.title}>Revenue Curve Forecast</Text>
                    <Text style={s.subtitle}>{projName}{versionLocked ? '  ·  version locked (read-only)' : ''}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    {loading && <ActivityIndicator />}
                    <View style={s.dataDatePill}><Text style={s.dataDateText}>Data Date: {dataDate}</Text></View>
                </View>
            </View>

            {/* Overview + instructions */}
            <View style={s.panel}>
                <Text style={s.panelTitle}>Overview</Text>
                <Text style={s.overviewText}>
                    Goal of this tab is to forecast all cost over time. Labor and Equipment pull from the Burn Tool,
                    but your job is to spread the remaining categories' cost out to produce a forecast.
                </Text>

                <View style={s.divider} />

                <Text style={s.panelTitle}>Instructions for Revenue Curve Inputs</Text>
                <View style={s.stepRow}>
                    <Text style={s.stepNum}>1</Text>
                    <Text style={s.stepText}>For each Cost Category, make sure forecast is finalized and all ETC values are correct.</Text>
                </View>
                <View style={s.stepRow}>
                    <Text style={s.stepNum}>2</Text>
                    <Text style={s.stepText}>In the Revenue Curve Input table, spread the remaining cost out over the forecast months until the remaining cost equals $0.</Text>
                </View>
            </View>

            {/* Cost curve input table */}
            <View style={s.panel}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <Text style={[s.panelTitle, { marginBottom: 0 }]}>REVENUE CURVE INPUT — ACTUALS TO DATE, THEN ETC BY MONTH</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Text style={s.horizonLabel}>
                            Horizon: through
                            {hiddenEtc ? `  ·  ${$(hiddenEtc)} of ETC sits past this window` : ''}
                        </Text>
                        <input
                            type="month"
                            value={horizonEnd.format('YYYY-MM')}
                            disabled={versionLocked}
                            onChange={(e) => {
                                if (/^\d{4}-\d{2}$/.test(e.target.value)) saveHorizon(moment(e.target.value + '-01'));
                            }}
                            title="End month for the chart and table window — saved with the forecast, so the PSR slide uses it too"
                            style={horizonInputStyle}
                        />
                        {overrideMonth ? (
                            <Pressable style={s.horizonBtn} disabled={versionLocked} onPress={() => saveHorizon(null)}>
                                <Text style={s.horizonBtnText}>Reset</Text>
                            </Pressable>
                        ) : null}
                        <Pressable style={s.horizonBtn} disabled={!overrideMonth && extraMonths <= 0} onPress={() => shiftHorizon(-5)}>
                            <Text style={[s.horizonBtnText, !overrideMonth && extraMonths <= 0 && { color: '#CBD5E1' }]}>− 5 mo</Text>
                        </Pressable>
                        <Pressable style={s.horizonBtn} onPress={() => shiftHorizon(5)}>
                            <Text style={s.horizonBtnText}>+ 5 mo</Text>
                        </Pressable>

                        <Pressable
                            style={s.horizonBtn}
                            onPress={exportExcel}
                            title="Download the month-by-month margin math as a formatted Excel workbook — rollup sheet with live formulas plus a category actual/ETC detail sheet"
                        >
                            <Text style={s.horizonBtnText}>Export</Text>
                        </Pressable>

                        <View style={{ position: 'relative' }}>
                            <Pressable style={[s.drillBtn, filterActive && s.drillBtnOn]} onPress={() => setFilterOpen((o) => !o)}>
                                <Text style={[s.drillBtnText, filterActive && { color: '#fff' }]}>
                                    ⛛ Filter{filterActive ? ' ●' : ''}
                                </Text>
                            </Pressable>
                            {filterOpen ? (
                                <View style={s.filterPop}>
                                    <Text style={s.filterPopTitle}>Filter Revenue Curve View</Text>

                                    <Text style={s.filterPopLabel}>Vendor</Text>
                                    <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)} style={popSelectStyle}>
                                        <option value="">All Vendors</option>
                                        {filterOptions.vendors.map((v) => <option key={v} value={v}>{v}</option>)}
                                    </select>

                                    <Text style={s.filterPopLabel}>Phase Code</Text>
                                    <select value={filterPhase} onChange={(e) => setFilterPhase(e.target.value)} style={popSelectStyle}>
                                        <option value="">All Phase Codes</option>
                                        {filterOptions.phases.map((p) => (
                                            <option key={p} value={p}>{p}{jobPhaseCodes?.[p] ? ` - ${jobPhaseCodes[p]}` : ''}</option>
                                        ))}
                                    </select>

                                    <Text style={s.filterPopLabel}>Resource Code</Text>
                                    <select value={filterResource} onChange={(e) => setFilterResource(e.target.value)} style={popSelectStyle}>
                                        <option value="">All Resource Codes</option>
                                        {filterOptions.resources.map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select>

                                    <View style={{ flexDirection: 'row', gap: 6, marginTop: 10 }}>
                                        <Pressable style={s.clearBtn} onPress={() => { setFilterVendor(''); setFilterPhase(''); setFilterResource(''); }}>
                                            <Text style={s.clearBtnText}>Clear</Text>
                                        </Pressable>
                                        <Pressable style={s.doneBtn} onPress={() => setFilterOpen(false)}>
                                            <Text style={s.doneBtnText}>Done</Text>
                                        </Pressable>
                                    </View>
                                </View>
                            ) : null}
                        </View>
                    </View>
                </View>

                <View style={{ overflowX: 'auto', width: '100%' }}>
                    {renderInputGrid(visibleMonths)}
                </View>
                <Text style={s.note}>
                    Months tagged “A” are actual — closed, showing real spend, and not editable. Months tagged “F” are forecast. The month of the selected Forecast Date is tagged “A+F” — not the month you happen to be reading this in: cost already posted into it counts as spent and is shown under the box, and the ETC you type covers only the rest of the month. Labor and Equipment come from the Burn Tool.
                    For the third-party rows, type the remaining estimate (ETC) directly into the future months — the Remaining Cost column updates as you go. “Clear” zeroes out the future months for that row.
                    {jobProfitPct != null
                        ? ` Earned Revenue is posted revenue for the closed “A” months; the “A+F” and “F” months earn their whole cost at the job's total JP of ${(jobProfitPct * 100).toFixed(2)}%, so their Job Margin reads back as that same JP% — the open month's billings don't count as earned until the month-end O/U accrual books.`
                        : ' Earned Revenue is posted revenue only — the job has no contract value yet, so no JP% is available to earn forecast cost at.'}
                </Text>

                {poolWarning ? (
                    <View style={[s.poolPop, poolWarning.at
                        ? { top: poolWarning.at.top, left: Math.max(8, Math.min(poolWarning.at.left, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 320)) }
                        : { top: 90, right: 20 }]}>
                        <Text style={s.poolPopTitle}>{$(poolWarning.tried)} not applied</Text>
                        <Text style={s.poolPopText}>
                            {ROWS.find((r) => r.key === poolWarning.key)?.label} has only {$(poolWarning.left)} left of the ETC entered on its Forecast page. Change the ETC there, not here.
                        </Text>
                        <Pressable style={s.poolPopBtn} onPress={() => setPoolWarning(null)}>
                            <Text style={s.poolPopBtnText}>OK</Text>
                        </Pressable>
                    </View>
                ) : null}
            </View>
            </>
            )}

            {/* Charts sit side by side, split 50/50 with each other, both
                interactively and on the PSR slide. On the slide the row
                also gets an explicit height (1.5x the measured table panel
                height, giving the chart block ~60% and the table ~40% of
                the stacked total) and an explicit width sized to match the
                slide content box's own aspect ratio (see photoCompositeWidth
                above) — without that, the tall narrow table drags the whole
                composite to a near-square shape, and addImageSlide's Revenue
                Curve branch, fitting this to a 9x4.75in box, has to shrink
                it down to a slim centered column to match its height. The
                table is left to size itself naturally (no forced height) so
                nothing here can clip it; photoVisibleMonths is what keeps it
                from needing the full composite width itself. Both live
                inside the same ref so charts and table land in one
                captured image. */}
            <View ref={chartsRef} style={a?.photo ? { maxWidth: photoCompositeWidth } : undefined}>
                <View style={[s.chartRow, a?.photo && { width: photoCompositeWidth }]}>
                    <View style={s.chartPanel}>
                        <Text style={s.panelTitle}>REVENUE CURVE (CUMULATIVE)</Text>
                        <View style={{ height: a?.photo ? photoChartFigureHeight : 260 }}><Line data={cumData} options={cumOptions} /></View>
                    </View>
                    <View style={s.chartPanel}>
                        <Text style={s.panelTitle}>COST BY PERIOD (MONTHLY)</Text>
                        <View style={{ height: a?.photo ? photoChartFigureHeight : 260 }}><Bar data={barData} options={barOptions} /></View>
                    </View>
                </View>

                {a?.photo ? (
                    // No overflow/scroll wrapper here (unlike the interactive
                    // table below) — this view is captured as a static image
                    // for the slide, and content clipped by a scroll
                    // container wouldn't make it into that capture. Filtering
                    // to photoVisibleMonths is what keeps this narrow instead.
                    // Centered (rather than flush-left) under the now much
                    // wider chart row above it.
                    <View ref={photoTablePanelRef} style={[s.panel, { marginTop: 16, alignSelf: 'center' }]}>
                        <Text style={[s.panelTitle, { marginBottom: 10 }]}>REVENUE CURVE INPUT — ACTUALS TO DATE, THEN ETC BY MONTH</Text>
                        {renderInputGrid(photoVisibleMonths)}
                    </View>
                ) : null}
            </View>
        </View>
    );
};

const horizonInputStyle = {
    height: 24, fontSize: 11, fontWeight: 600, color: '#334155',
    padding: '0 6px', borderRadius: 6, border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF', cursor: 'pointer', outline: 'none',
};

const popSelectStyle = {
    width: '100%', fontSize: 12, padding: '6px 8px', borderRadius: 6,
    border: '1px solid #CBD5E1', backgroundColor: '#FFFFFF', color: '#1E293B',
    cursor: 'pointer', outline: 'none',
};

const s = StyleSheet.create({
    empty: { flex: 1, justifyContent: 'center', alignItems: 'center', minHeight: 400 },
    emptyText: { fontSize: 15, color: '#64748B' },
    muted: { fontSize: 11, color: '#94A3B8' },

    headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
    title: { fontSize: 22, fontWeight: '700', color: '#0F172A' },
    subtitle: { fontSize: 13, color: '#64748B', marginTop: 2 },
    dataDatePill: { height: 28, paddingLeft: 12, paddingRight: 12, border: '1px solid #E2E8F0', borderRadius: 8, backgroundColor: '#FFFFFF', justifyContent: 'center' },
    dataDateText: { fontSize: 12, color: '#334155', fontWeight: '600' },

    panel: { backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10, padding: 14, marginBottom: 16 },
    panelTitle: { fontSize: 11, fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 },
    note: { fontSize: 11, color: '#94A3B8', marginTop: 10 },
    overviewText: { fontSize: 13, color: '#334155', lineHeight: 19 },
    divider: { height: 1, backgroundColor: '#E2E8F0', marginTop: 14, marginBottom: 14 },
    stepRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 8 },
    stepNum: { width: 18, height: 18, borderRadius: 9, backgroundColor: '#1D4ED8', color: '#FFFFFF', fontSize: 10, fontWeight: '700', textAlign: 'center', lineHeight: 18, marginRight: 8 },
    stepText: { flex: 1, fontSize: 13, color: '#334155', lineHeight: 19 },
    horizonLabel: { fontSize: 11, color: '#64748B', marginRight: 4 },
    horizonBtn: { height: 24, paddingLeft: 10, paddingRight: 10, border: '1px solid #CBD5E1', borderRadius: 6, justifyContent: 'center', backgroundColor: '#FFFFFF' },
    horizonBtnText: { fontSize: 11, color: '#334155', fontWeight: '600' },
    drillBtn: { height: 24, paddingLeft: 10, paddingRight: 10, border: '1px solid #CBD5E1', borderRadius: 6, justifyContent: 'center', backgroundColor: '#FFFFFF' },
    drillBtnOn: { backgroundColor: '#1D4ED8', border: '1px solid #1D4ED8' },
    drillBtnText: { fontSize: 11, color: '#334155', fontWeight: '600' },
    filterPop: { position: 'absolute', top: '100%', right: 0, marginTop: 4, backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.12)', padding: 14, zIndex: 100, minWidth: 250 },
    filterPopTitle: { fontSize: 13, fontWeight: '700', color: '#0F172A', marginBottom: 10 },
    filterPopLabel: { fontSize: 11, fontWeight: '700', color: '#1D4ED8', marginBottom: 3, marginTop: 6 },
    doneBtn: { height: 22, paddingLeft: 10, paddingRight: 10, backgroundColor: '#1D4ED8', borderRadius: 4, justifyContent: 'center' },
    doneBtnText: { fontSize: 10, color: '#fff', fontWeight: '700' },

    gridRow: { flexDirection: 'row' },
    totalRow: { backgroundColor: '#FEF3C7' },
    jpLabelCell: { backgroundColor: '#E2E8F0' },
    headCell: { minHeight: 44, border: '1px solid #CBD5E1', padding: '4px 6px', backgroundColor: '#F1F5F9', justifyContent: 'center' },
    pastHead: { backgroundColor: '#E2E8F0' },
    headText: { fontSize: 14, fontWeight: '600', color: '#334155', textAlign: 'right' },
    // Overrides a header cell's column alignment (several are right/center-
    // aligned to match their numeric values) so the label itself sits left.
    headTextLeft: { textAlign: 'left', alignSelf: 'flex-start' },
    cell: { minHeight: 28, border: '1px solid #E2E8F0', padding: '4px 6px', justifyContent: 'center' },
    readonlyCell: { backgroundColor: '#F8FAFC' },
    remainingOver: { backgroundColor: '#FEE2E2' },
    remainingClear: { backgroundColor: '#ECFDF5' },
    labelCol: { width: 150, alignItems: 'flex-start' },
    remainingCol: { width: 170, alignItems: 'flex-end' },
    // Editable rows carry a Clear button alongside the remaining-cost figure.
    remainingCellRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    monthCol: { width: 128, alignItems: 'flex-end' },
    labelText: { fontSize: 16, color: '#0F172A' },
    numText: { fontSize: 14, color: '#0F172A', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    cellInputBad: { backgroundColor: '#FEE2E2', border: '1.5px solid #DC2626', color: '#B91C1C', fontWeight: '700' },
    poolPop: { position: 'fixed', width: 300, backgroundColor: '#FEF2F2', border: '1px solid #DC2626', borderRadius: 8, boxShadow: '0 10px 28px rgba(15,23,42,0.18)', padding: 12, zIndex: 1000 },
    poolPopTitle: { fontSize: 12, fontWeight: '700', color: '#B91C1C', marginBottom: 4 },
    poolPopText: { fontSize: 11, color: '#7F1D1D', lineHeight: 16 },
    poolPopBtn: { alignSelf: 'flex-end', marginTop: 10, paddingTop: 3, paddingBottom: 3, paddingLeft: 14, paddingRight: 14, backgroundColor: '#DC2626', borderRadius: 4 },
    poolPopBtnText: { fontSize: 11, color: '#fff', fontWeight: '700' },
    monthTag: { fontSize: 8, fontWeight: '700', lineHeight: 12, minWidth: 12, textAlign: 'center', borderRadius: 3, paddingLeft: 3, paddingRight: 3 },
    monthTagA: { color: '#475569', backgroundColor: '#E2E8F0' },
    monthTagF: { color: '#1D4ED8', backgroundColor: '#DBEAFE' },
    monthTagAF: { color: '#B45309', backgroundColor: '#FEF3C7' },
    // monthCol right-aligns its cell's children (alignItems: 'flex-end'), so
    // without an explicit width this row shrinks to its content and anchors
    // right — any overflow spills left into the previous month instead of
    // clipping. width: '100%' pins it to the column so overflow: hidden below
    // actually has a boundary to clip against. minWidth: 0 on the input keeps
    // it from claiming its default intrinsic width for the same reason.
    splitHalf: { width: '100%', height: 22, flexDirection: 'row', alignItems: 'center', overflow: 'hidden', paddingLeft: 6, paddingRight: 6 },
    splitHalfBottom: { borderTop: '1px solid #E2E8F0' },
    splitTag: { marginRight: 4, flexShrink: 0 },
    splitInput: { flex: 1, minWidth: 0, height: 22, border: 'none', padding: '0 4px', fontSize: 14, textAlign: 'right', color: '#0F172A', backgroundColor: '#FFF7ED' },
    splitActualValue: { flex: 1, minWidth: 0, fontSize: 14, textAlign: 'right', color: '#94A3B8', fontVariantNumeric: 'tabular-nums', paddingRight: 4 },

    cellInput: { width: '100%', height: 32, border: 'none', padding: '0 6px', fontSize: 14, textAlign: 'right', color: '#0F172A', backgroundColor: '#FFF7ED' },
    clearBtn: { marginLeft: 4, height: 22, paddingLeft: 8, paddingRight: 8, backgroundColor: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 4, justifyContent: 'center' },
    clearBtnText: { fontSize: 10, color: '#64748B', fontWeight: '600' },

    chartRow: { flexDirection: 'row', gap: 16 },
    // flexBasis: 0 with matching flexGrow on both panels splits the row
    // exactly 50/50 regardless of each chart's own content width.
    chartPanel: { flexGrow: 1, flexBasis: 0, minWidth: 0, backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10, padding: 14 },
});

export default CostCurveForecast;
